import { SCORING_OPENS_AT } from "./season";

// What a league card should say about where that league is up to.
//
// /home lists leagues and said nothing about any of them beyond the name, so
// a manager with three leagues could not tell which one was mid-draft, which
// was waiting on them, and which was scoring. All of it is derivable from
// rows already being read.
//
// Pure, so the cases can be tested without a database. The caller supplies
// what it loaded; this decides what it means.
export const LEAGUE_STATUS = {
  drafting: "drafting",
  predraft: "predraft",
  ready: "ready",
  live: "live",
};

/**
 * @param {object} league        { draft_complete }
 * @param {object|null} draft    { status, current_pick, total_rounds }
 * @param {object} standing      { memberCount, rank, points, teamName }
 * @param {Date}   now
 */
export function leagueStatus({ league, draft, standing = {}, now = new Date() } = {}) {
  const { memberCount = 0, rank = null, points = null, teamName = null } = standing;

  if (league?.draft_complete) {
    // The season has not started until the first scoring kickoff. Week 0 plays
    // a full weekend and awards nothing, so a league that has drafted is
    // waiting rather than playing.
    const scoringStarted = now.getTime() >= new Date(SCORING_OPENS_AT).getTime();

    if (!scoringStarted) {
      return { key: LEAGUE_STATUS.ready, label: "Ready for kickoff", detail: null };
    }

    return {
      key: LEAGUE_STATUS.live,
      // Ordinal rather than "4/12", which reads as a score.
      label: rank ? `${ordinal(rank)} of ${memberCount}` : "In season",
      // Team name leads: a bare point total on a card listing several
      // leagues gives no way to tell whose score it is at a glance.
      detail: points == null ? null : teamName ? `${teamName} · ${points} pts` : `${points} pts`,
    };
  }

  if (draft?.status === "active") {
    const pick = Number(draft.current_pick) || 0;
    const total = totalPicks(draft, memberCount);
    return {
      key: LEAGUE_STATUS.drafting,
      label: "Drafting now",
      // current_pick is how many picks have been made, so the pick on the
      // clock is the next one.
      detail: total ? `Pick ${Math.min(pick + 1, total)} of ${total}` : null,
    };
  }

  return {
    key: LEAGUE_STATUS.predraft,
    label: "Draft not started",
    detail: memberCount ? `${memberCount} ${memberCount === 1 ? "manager" : "managers"}` : null,
  };
}

function totalPicks(draft, memberCount) {
  const rounds = Number(draft?.total_rounds) || 0;
  return rounds && memberCount ? rounds * memberCount : 0;
}

// 1st, 2nd, 3rd, 4th, and the 11/12/13 exceptions.
export function ordinal(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  const rem100 = num % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${num}th`;
  switch (num % 10) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
}
